import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { API_ROUTES } from 'utils/constants';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { signInWitGoogleAsync } from './redux-toolkit/store/reducers/auth.reducer';
import AuthIllustration from "assets/img/auth/auth.webp";
import { FcGoogle } from "react-icons/fc";

const Signup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const queryParams = new URLSearchParams(location.search);

  const [formData, setFormData] = useState({
    fullName: '',
    username: '',
    email: '',
    password: '',
    passwordConfirm: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);

  // Force dark mode on component mount
  useEffect(() => {
    // Add dark mode class to html and body
    document.documentElement.classList.add('dark');
    document.body.classList.add('dark');
    
    // Optional: Remove light theme if present
    document.documentElement.classList.remove('light');
    document.body.classList.remove('light');
    
    return () => {
      // If you want to clean up when component unmounts (usually not needed)
      // document.documentElement.classList.remove('dark');
      // document.body.classList.remove('dark');
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Form validation
    if (!formData.fullName || !formData.username || !formData.email || !formData.password) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.password !== formData.passwordConfirm) {
      setError('Passwords do not match');
      return;
    }

    if (!agreeTerms) {
      setError('Please agree to the terms and conditions');
      return;
    }

    setIsLoading(true);

    try {
      // Use axios for better error handling
      const response = await axios.post(API_ROUTES.SIGN_UP, formData, {
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.status === 201) {
        console.log('Signup successful');
        navigate('/login');
      }
    } catch (error) {
      console.error('Error during signup:', error);
      setError(error.response?.data?.message || 'Signup failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Google Sign Up
  const handleGoogleSignUp = async () => {
    try {
      // Initialize Google Auth - make sure to include the Google OAuth client in your index.html
      const auth2 = window.gapi.auth2.getAuthInstance();
      
      if (!auth2) {
        console.error("Google Auth is not initialized");
        setError("Google authentication is not available at the moment");
        return;
      }
      
      // Start Google sign-in flow
      setIsLoading(true);
      const googleUser = await auth2.signIn();
      const idToken = googleUser.getAuthResponse().id_token;
      
      // Send Google token to our backend
      const response = await dispatch(signInWitGoogleAsync({ token: idToken })).unwrap();
      
      // Handle successful authentication
      if (response.token) {
        // Store the token
        sessionStorage.setItem('token', response.token);
        
        // Update state in localStorage
        try {
          const currentState = JSON.parse(localStorage.getItem('state') || '{}');
          const updatedState = {
            ...currentState,
            auth: {
              ...currentState.auth,
              isAuthenticated: true,
              user: response,
            }
          };
          localStorage.setItem('state', JSON.stringify(updatedState));
        } catch (err) {
          console.error("Error saving auth state to localStorage:", err);
        }
        
        // Navigate to dashboard
        navigate('/dashboard');
      }
    } catch (error) {
      console.error("Google sign-up error:", error);
      setError("Failed to sign up with Google. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Load Google SDK
  useEffect(() => {
    // Load the Google SDK script
    const loadGoogleScript = () => {
      // Check if script is already loaded
      if (document.querySelector('script[src="https://apis.google.com/js/platform.js"]')) {
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/platform.js';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogleAuth;
      document.head.appendChild(script);
    };

    // Initialize Google Auth after script loads
    const initializeGoogleAuth = () => {
      window.gapi.load('auth2', () => {
        window.gapi.auth2.init({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        }).then(() => {
          console.log("Google Auth initialized");
        }).catch(err => {
          console.error("Error initializing Google Auth:", err);
        });
      });
    };

    loadGoogleScript();
  }, []);

  return (
    <div className="relative float-right h-full min-h-screen w-full !bg-navy-900">
      <main className="mx-auto min-h-screen">
        <div className="relative flex">
          <div className="mx-auto flex min-h-full w-full flex-col justify-start pt-12 md:max-w-[75%] lg:h-screen lg:max-w-[1013px] lg:px-8 lg:pt-0 xl:h-[100vh] xl:max-w-[1383px] xl:px-0 xl:pl-[70px]">
            <div className="mb-auto flex flex-col pl-5 pr-5 md:pl-12 md:pr-0 lg:max-w-[48%] lg:pl-0 xl:max-w-full">
              <div className="mt-16 mb-16 flex h-full w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:mb-10 lg:items-center lg:justify-start">
                {/* Sign up form */}
                <div className="w-full max-w-full flex-col items-center md:pl-4 lg:pl-0 xl:max-w-[420px]">
                  <h4 className="mb-2.5 text-4xl font-bold text-white">
                    Sign Up
                  </h4>
                  <p className="mb-9 ml-1 text-base text-gray-400">
                    Create your account to get started!
                  </p>

                  {/* <div className="mb-6 flex h-[50px] w-full items-center justify-center gap-2 rounded-xl bg-navy-800 hover:cursor-pointer hover:bg-navy-700 transition-all"
                       onClick={handleGoogleSignUp}>
                    <div className="rounded-full text-xl">
                      <FcGoogle />
                    </div>
                    <h5 className="text-sm font-medium text-white">
                      Sign Up with Google
                    </h5>
                  </div>
                  
                  <div className="mb-6 flex items-center gap-3">
                    <div className="h-px w-full bg-navy-700" />
                    <p className="text-base text-white"> or </p>
                    <div className="h-px w-full bg-navy-700" />
                  </div> */}

                  {error && (
                    <div className="mb-4 rounded-md bg-red-900/30 p-4 text-sm text-red-400">
                      {error}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="flex flex-col w-full space-y-4">
                    {/* Full Name */}
                    <div>
                      <label htmlFor="fullName" className="text-sm text-white">Full Name*</label>
                      <input
                        id="fullName"
                        name="fullName"
                        type="text"
                        value={formData.fullName}
                        onChange={handleChange}
                        placeholder="John Doe"
                        required
                        className="mt-2 block h-12 w-full rounded-xl border border-navy-700 bg-navy-900 px-4 text-sm font-medium text-white outline-none focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    
                    {/* Username */}
                    <div>
                      <label htmlFor="username" className="text-sm text-white">Username*</label>
                      <input
                        id="username"
                        name="username"
                        type="text"
                        value={formData.username}
                        onChange={handleChange}
                        placeholder="johndoe"
                        required
                        className="mt-2 block h-12 w-full rounded-xl border border-navy-700 bg-navy-900 px-4 text-sm font-medium text-white outline-none focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label htmlFor="email" className="text-sm text-white">Email*</label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        required
                        className="mt-2 block h-12 w-full rounded-xl border border-navy-700 bg-navy-900 px-4 text-sm font-medium text-white outline-none focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>

                    {/* Password */}
                    <div>
                      <label htmlFor="password" className="text-sm text-white">Password*</label>
                      <input
                        id="password"
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Min. 8 characters"
                        required
                        className="mt-2 block h-12 w-full rounded-xl border border-navy-700 bg-navy-900 px-4 text-sm font-medium text-white outline-none focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    
                    {/* Confirm Password */}
                    <div>
                      <label htmlFor="passwordConfirm" className="text-sm text-white">Confirm Password*</label>
                      <input
                        id="passwordConfirm"
                        name="passwordConfirm"
                        type="password"
                        value={formData.passwordConfirm}
                        onChange={handleChange}
                        placeholder="Confirm your password"
                        required
                        className="mt-2 block h-12 w-full rounded-xl border border-navy-700 bg-navy-900 px-4 text-sm font-medium text-white outline-none focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    
                    {/* Terms Checkbox */}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="terms"
                        checked={agreeTerms}
                        onChange={() => setAgreeTerms(!agreeTerms)}
                        className="h-4 w-4 rounded border-navy-700 bg-navy-900 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="terms" className="ml-2 block text-sm text-white">
                        I agree to the{" "}
                        <a href="#" className="text-blue-400 hover:text-blue-300">
                          Terms and Conditions
                        </a>
                      </label>
                    </div>
                    
                    <button 
                      type="submit"
                      disabled={isLoading}
                      className="linear mt-2 w-full rounded-xl bg-blue-600 py-[12px] text-base font-medium text-white transition duration-200 hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50"
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center">
                          <div className="mr-2 h-4 w-4 border-l-2 rounded-full animate-spin"></div>
                          <span>SIGNING UP...</span>
                        </div>
                      ) : "SIGN UP"}
                    </button>
                  </form>
                  
                  <div className="mt-4 text-center">
                    <span className="text-sm font-medium text-gray-400">
                      Already have an account?{" "}
                      <Link
                        to="/login"
                        className="text-sm font-medium text-blue-400 hover:text-blue-300"
                      >
                        Sign In
                      </Link>
                    </span>
                  </div>
                </div>
              </div>
              <div className="absolute right-0 hidden h-full min-h-screen md:block lg:w-[49vw] 2xl:w-[44vw]">
                <div
                  className="absolute flex h-full w-full items-end justify-center bg-cover bg-center lg:rounded-bl-[120px] xl:rounded-bl-[200px]"
                  style={{ backgroundImage: `url(${AuthIllustration})` }}
                />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Signup;
